import { Button } from '@selina-finance/ui';

import { toCurrency } from '@src/shared/helpers/numbers';

interface ReviewCalculationViewProps {
    closeModal: () => void;
    initialPayment: string;
    totalMonthlyPayment: string;
    estimatedChange: number;
    loanAmount: string;
    totalConsolidation: string;
    otherPurpose: string;
    totalEstimatedPayments: string;
    estimatedReductionInPayments: number;
}

const ReviewCalculationView = ({
    closeModal,
    initialPayment,
    totalMonthlyPayment,
    estimatedChange,
    loanAmount,
    totalConsolidation,
    otherPurpose,
    totalEstimatedPayments,
    estimatedReductionInPayments,
}: ReviewCalculationViewProps) => {
    const estimatedChangeLabel =
        estimatedChange < 0 ? 'Estimated increase' : 'Estimated decrease';

    const reductionInPaymentsLabel =
        estimatedReductionInPayments < 0
            ? 'Estimated monthly increase in debt payments'
            : 'Estimated monthly reduction in debt payments';

    const PaymentRow = ({
        label,
        value,
        isHighlighted = false,
        bgColor = 'bg-white',
        borderColor = '',
        extraClasses = '',
        labelWidth = '',
    }: {
        label: string;
        value: string;
        isHighlighted?: boolean;
        bgColor?: string;
        borderColor?: string;
        extraClasses?: string;
        labelWidth?: string;
    }) => (
        <div
            className={`flex justify-between p-3 text-sm ${bgColor} ${
                isHighlighted ? `border-t ${borderColor}` : ''
            } ${extraClasses}`}
        >
            <span className={labelWidth}>{label}</span>
            <span className='font-sora-bold text-midnight-200'>
                &pound;{value}
            </span>
        </div>
    );

    const ConfirmButton = ({ onClick }: { onClick: () => void }) => (
        <div className='flex flex-col items-center'>
            <Button
                size='large'
                color='tertiary-flat'
                className='w-full lg:max-w-400 text-lg'
                onClick={onClick}
            >
                <span className='text-lg font-sora-bold'>Okay</span>
            </Button>
        </div>
    );

    return (
        <div className='mx-auto pt-6 lg:p-10'>
            <div className='mb-4'>
                <h2 className='text-lg font-bold mb-2 text-midnight-200'>
                    Loan details
                </h2>
                <PaymentRow
                    label='Loan amount'
                    value={loanAmount}
                    extraClasses='rounded mb-2'
                />
                <PaymentRow
                    label='For debt consolidation'
                    value={totalConsolidation}
                    extraClasses='mb-0.5'
                />
                <PaymentRow
                    label='For other purposes'
                    value={otherPurpose}
                    extraClasses='rounded-b'
                />
            </div>

            <div className='mb-4'>
                <h2 className='text-lg font-bold mb-2 text-midnight-200'>
                    Total monthly payment
                </h2>
                <PaymentRow
                    label='Current combined debt payments'
                    value={totalMonthlyPayment}
                    extraClasses='rounded-t mb-0.5'
                />
                <PaymentRow
                    label='Estimated Selina monthly payment'
                    value={initialPayment}
                    labelWidth='w-44 lg:w-full'
                />
                <PaymentRow
                    label={estimatedChangeLabel}
                    value={toCurrency(Math.abs(estimatedChange))}
                    isHighlighted
                    bgColor='bg-peach-100'
                    borderColor='border-peach-500'
                />
            </div>

            <div className='mb-5'>
                <h2 className='text-lg font-bold mb-2 text-midnight-200'>
                    Debt monthly payment
                </h2>
                <PaymentRow
                    label='Current combined debt payments'
                    value={totalMonthlyPayment}
                    extraClasses='rounded-t mb-0.5'
                />
                <PaymentRow
                    label='Estimated Selina monthly payment towards debts*'
                    value={totalEstimatedPayments}
                    labelWidth='w-44 lg:w-full'
                />
                <PaymentRow
                    label={reductionInPaymentsLabel}
                    value={toCurrency(Math.abs(estimatedReductionInPayments))}
                    isHighlighted
                    bgColor='bg-peppermint-100'
                    borderColor='border-peppermint-500'
                    labelWidth='w-40 lg:w-full'
                />
            </div>

            <ConfirmButton onClick={closeModal} />
        </div>
    );
};

export default ReviewCalculationView;
